import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import { request } from "@/shared/http/client";

/**
 * O documento do workspace — e os três interruptores de exibição do agente.
 *
 * ── POR QUE ISTO MORA EM `shared/` ────────────────────────────────────────
 *
 * Os interruptores são gravados na tela de conta (`features/workspace`) e lidos no chat
 * com os agentes (`features/agents`). Uma feature nunca importa outra (`no-cross-feature`,
 * severidade error), e o remédio está escrito na própria regra: "share through shared/ or
 * an event". É isto — a mesma porta que `shared/access/use-membership.ts` já usa.
 *
 * A leitura mora aqui INTEIRA, e não uma cópia da query dentro de cada feature: duas
 * consultas ao mesmo `/v3/workspace` com dois `staleTime` é como uma tela passa a mostrar
 * o interruptor de antes de alguém tê-lo desligado. `features/workspace/api/use-workspace.ts`
 * importa daqui e continua sendo o dono das escritas.
 */
export interface AgentDisplay {
	readonly showReasoning: boolean;
	readonly showTools: boolean;
	readonly showCost: boolean;
}

/**
 * Como uma resposta de PESSOA é assinada a caminho do cliente (migração 0057).
 *
 * Uma decisão, não as três da V2 (atendente / agente / robô, cada uma com título): um
 * robô não tem nome que o cliente possa chamar, e um workspace que queira o assistente
 * se apresentando escreve isso na persona, que é onde a voz do assistente já mora.
 */
export type SignatureMode = "none" | "name" | "title" | "name_title";

export interface Signature {
	readonly mode: SignatureMode;
	/** A palavra que representa o papel. Só os dois modos com título a leem. */
	readonly title: string | null;
}

export const DEFAULT_SIGNATURE: Signature = { mode: "none", title: null };

export interface Workspace {
	/**
	 * O endereço público deste workspace — SÓ LEITURA.
	 *
	 * Volta no `GET /v3/workspace` desde a fase F; o `PATCH` não o aceita e o repositório
	 * não tem linha de `SET` para ele. Todo link `/s/`, `/f/` e `/c/` já em circulação foi
	 * montado a partir dele, e o schema do banco do tenant é derivado dele — trocá-lo
	 * quebraria os dois em silêncio. Nenhuma tela deve oferecer edição.
	 */
	readonly slug: string;
	readonly name: string;
	/**
	 * A marca do workspace, como ID do arquivo guardado — nunca uma URL (I-S5).
	 *
	 * Viaja no mesmo documento que o nome porque é lida no mesmo instante que ele: o
	 * cabeçalho do painel desenha os dois lado a lado. Quem transforma o id em
	 * endereço é a porta de upload (`useFileSrc`), e as telas públicas nem passam por
	 * aqui — elas têm a rota pública, que não pede sessão.
	 */
	readonly logoFileId: string | null;
	readonly language: string;
	readonly currency: string;
	readonly timezone: string;
	readonly country: string;
	readonly agentDisplay: AgentDisplay;
	readonly signature: Signature;
}

/**
 * O que o cliente lê antes das palavras, para um remetente.
 *
 * A mesma regra do `signatureLabel` do servidor, aqui pela razão pela qual a prévia
 * existe: um workspace só decide entre "nome" e "nome e título" vendo os dois. String
 * vazia significa "sai como foi escrito" — e é a resposta quando não há nome a dizer,
 * porque `*:*` na frente da mensagem de um cliente é pior que assinatura nenhuma.
 */
/**
 * TRAVESSÃO, e não hífen — o mesmo separador do `signatureLabel` do servidor.
 *
 * A dica do modo promete "os dois, separados por travessão"; a prévia escrevia um
 * hífen, e a prévia é justamente o que se olha para decidir o modo.
 */
const NAME_TITLE_SEPARATOR = "—";

export function signatureLabel(signature: Signature, senderName: string | null): string {
	const name = senderName?.trim() || null;
	const title = signature.title?.trim() || null;
	if (signature.mode === "name") return name ?? "";
	if (signature.mode === "title") return title ?? "";
	if (signature.mode === "name_title") {
		return name && title ? `${name} ${NAME_TITLE_SEPARATOR} ${title}` : (name ?? title ?? "");
	}
	return "";
}

/**
 * O que os interruptores significam antes de o workspace carregar, ou numa tela que não
 * pode esperar por ele: tudo visível, que é o que toda tela fazia antes de eles existirem.
 *
 * A coluna nasce `'{}'` ("não decidido") de propósito, então quem afirma o padrão é a
 * superfície — mudar o padrão depois alcança quem nunca mexeu nos interruptores.
 */
export const DEFAULT_AGENT_DISPLAY: AgentDisplay = {
	showReasoning: true,
	showTools: true,
	showCost: true,
};

/** A chave do documento. As mutações da feature invalidam exatamente esta. */
export const workspaceSelfKey = ["workspace"] as const;

export function useWorkspaceDoc(enabled: () => boolean = () => true) {
	return useQuery({
		enabled,
		queryKey: workspaceSelfKey,
		queryFn: () => request<{ workspace: Workspace }>("/v3/workspace"),
		select: (data) => data.workspace,
		// Read once per session in practice: the shell formats everything off this, so
		// a refetch mid-session would reformat the page under someone's cursor.
		staleTime: 5 * 60_000,
	});
}

/**
 * As portas públicas do produto. `site` é o workspace inteiro; as outras, uma coisa
 * publicada. `form-embed` é o MESMO formulário sem a moldura, para dentro de um iframe
 * no site de outra pessoa — é uma rota real (`/embed/f/:slug`), não uma variação de estilo.
 */
export type PublicKind = "site" | "form" | "form-embed" | "chat";

const PUBLIC_PATH: Record<PublicKind, string> = {
	site: "/s",
	form: "/f",
	"form-embed": "/embed/f",
	chat: "/c",
};

/**
 * O endereço ABSOLUTO de alguma coisa publicada por este workspace.
 *
 * Montado no front, e não devolvido pela API, pela razão já escrita em
 * `use-channels.ts`: a API não sabe de onde o app é servido, e um endereço com a origem
 * errada parece que a coisa publicada quebrou, não que o link está errado.
 *
 * Mora em `shared/` porque quem precisa dele são duas features que não podem se importar
 * (a conta mostra `/s/<slug>`, a estrutura mostra `/f/<slug>` de cada formulário no ar) —
 * e o mesmo cálculo em dois lugares é como um deles fica para trás.
 */
export function publicUrl(kind: PublicKind, slug: string): string {
	return `${window.location.origin}${PUBLIC_PATH[kind]}/${slug}`;
}

/**
 * Os três interruptores, para um componente que só precisa deles.
 *
 * Um composable e não uma prop atravessando quatro telas: `StreamingChat` é montado pela
 * página de agentes, pelo orbe e pelo chat público, e enfiar uma preferência pelos três
 * caminhos seriam três chances de esquecer.
 */
export function useAgentDisplay() {
	const { data } = useWorkspaceDoc();
	return computed(() => data.value?.agentDisplay ?? DEFAULT_AGENT_DISPLAY);
}
